import { useCallback, useEffect, useRef, useState } from 'react'
import {
  FilterType,
  getFilteredTodoItems,
  getFilteredTodoItemsDB,
} from '../../../modules/TodoFilter'
import { TodoFolder } from '../../../modules/TodoFolder'
import { getFolders, getSortedFoldersAsync } from '../../../modules/TodoFolderSync'
import { TodoItem } from '../../../modules/TodoItem'
import * as todoService from '../../../modules/TodoService'
import * as todoSync from '../../../modules/TodoSync'
import { isTodoDBEnabled, isTodoDBReady } from '../../../modules/todoDB/index'

interface UseTodoListReturn {
  todoList: TodoItem[]
  folders: TodoFolder[]
  loadTodos: () => Promise<void>
  setTodoList: React.Dispatch<React.SetStateAction<TodoItem[]>>
}

export const useTodoList = (
  currentFilter: FilterType,
  focusDate: Date | null,
): UseTodoListReturn => {
  const [todoList, setTodoList] = useState<TodoItem[]>([])
  const [folders, setFolders] = useState<TodoFolder[]>([])
  const [_isSyncing, setIsSyncing] = useState<boolean>(false)
  const loadingRef = useRef<boolean>(false)

  // 최신 값들을 참조하기 위한 ref
  const currentFilterRef = useRef<FilterType>(currentFilter)
  const focusDateRef = useRef<Date | null>(focusDate)

  // ref 값 업데이트
  currentFilterRef.current = currentFilter
  focusDateRef.current = focusDate

  const loadTodos = useCallback(async () => {
    if (loadingRef.current) {
      return
    }

    try {
      loadingRef.current = true
      const filter = currentFilterRef.current
      const dateFilter = focusDateRef.current
      let filteredTodos: TodoItem[] = []
      console.log(`[useTodoList] Loading todos for filter: ${filter}`)

      // DB가 활성화되고 준비된 경우 DB에서 먼저 조회
      if (isTodoDBEnabled() && isTodoDBReady()) {
        console.log('[useTodoList] Using DB-based queries')
        try {
          if (filter && filter !== FilterType.All) {
            filteredTodos = await getFilteredTodoItemsDB(filter, dateFilter)
          } else {
            filteredTodos = (await todoService.getSyncTodoItems()) || []
          }
          console.log(`[useTodoList] DB query result: ${filteredTodos.length} todos`)
        } catch (dbError) {
          console.warn('TodoList: DB 조회 실패, 메모리 방식으로 전환:', dbError)
          // DB 실패 시 기존 메모리 방식으로 fallback
          if (filter && filter !== FilterType.All) {
            console.log(`[useTodoList] Fallback: Getting filtered todos from memory for: ${filter}`)
            filteredTodos = await getFilteredTodoItems(filter, dateFilter)
          } else {
            console.log('[useTodoList] Fallback: Getting sync todos from memory')
            filteredTodos = (await todoService.getSyncTodoItems()) || []
          }
          console.log(`[useTodoList] Fallback result: ${filteredTodos.length} todos`)
        }
      } else {
        console.log('[useTodoList] Using memory-based queries')
        // DB 비활성화 상태일 때는 기존 메모리 방식 사용
        if (filter && filter !== FilterType.All) {
          filteredTodos = await getFilteredTodoItems(filter, dateFilter)
        } else {
          filteredTodos = (await todoService.getSyncTodoItems()) || []
        }
        console.log(`[useTodoList] Memory result: ${filteredTodos.length} todos`)
      }

      // DB 우선으로 정렬된 폴더 조회
      let folderList: TodoFolder[] = []
      try {
        folderList = await getSortedFoldersAsync()
      } catch (error) {
        console.warn(
          '[useTodoList] Failed to get sorted folders from DB, falling back to memory:',
          error,
        )
        folderList = getFolders ? getFolders() : []
      }
      setFolders(folderList)
      setTodoList(filteredTodos)
    } catch (error) {
      console.error('TodoList: Error loading todos:', error)
      setTodoList([])
    } finally {
      loadingRef.current = false
    }
  }, [])

  // 이벤트 리스너 등록 (컴포넌트 마운트 시 한 번만)
  useEffect(() => {
    const handleFolderUpdate = () => {
      console.log('[useTodoList] Folder update received - reloading todos')
      loadTodos()
    }

    const handleItemUpdate = () => {
      console.log('[useTodoList] Item update received - reloading todos')
      loadTodos()
    }

    const handleSyncStatus = (syncing: boolean) => {
      console.log('TodoList: Sync status changed:', syncing)
      setIsSyncing(syncing)
    }

    todoSync.addTodoFolderListener(handleFolderUpdate)
    todoSync.addTodoItemListener(handleItemUpdate)
    todoSync.addTodoSyncListener(handleSyncStatus)

    return () => {
      todoSync.removeTodoFolderListener(handleFolderUpdate)
      todoSync.removeTodoItemListener(handleItemUpdate)
      todoSync.removeTodoSyncListener(handleSyncStatus)
    }
  }, [loadTodos]) // loadTodos 의존성 추가

  // 데이터 로딩 (필터, 날짜 변경 시)
  // syncFolders 변경에 의한 reload는 addTodoFolderListener가 처리하므로 의존성에서 제외
  useEffect(() => {
    loadTodos()
  }, [currentFilter, focusDate, loadTodos])

  return { todoList, folders, loadTodos, setTodoList }
}
